/**
 * Sockologie Upsell — shared frontend client
 *
 * Provides two custom elements:
 *   <upsell-fbt>  — Frequently Bought Together on PDP
 *   <upsell-cart> — Recommendations on cart page / drawer
 *
 * Configuration is injected by the Liquid sections:
 *   window.UPSELL_CONFIG = { serviceUrl, shopifyRecsUrl }
 *
 * No build-step dependencies — vanilla JS, loads as type="module".
 */

// ── Inject styles (once) so they work inside custom-liquid blocks too ─────────

(function injectUpsellStyles() {
  if (document.getElementById('upsell-styles')) return;
  const s = document.createElement('style');
  s.id = 'upsell-styles';
  s.textContent = `
    upsell-fbt { display: block; }
    upsell-fbt:empty { display: none; }
    upsell-fbt[hidden] { display: none; }

    .upsell-fbt__head-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin-block-end: 14px;
    }

    .upsell-fbt__heading {
      font-size: 22px;
      font-weight: 700;
      letter-spacing: 0.01em;
      text-transform: none;
      margin: 0;
      color: var(--color-foreground);
    }

    .upsell-fbt__pill {
      background: #E9B024;
      color: #1c1c1c;
      font-size: 0.72rem;
      font-weight: 800;
      letter-spacing: 0.02em;
      padding: 3px 10px;
      border-radius: 20px;
      white-space: nowrap;
      flex-shrink: 0;
    }

    /* Full width, single flex row */
    .upsell-fbt__inner {
      width: 100%;
    }

    .upsell-fbt__box {
      display: flex;
      align-items: flex-start;
      gap: 20px;
      width: 100%;
    }

    .upsell-fbt__products {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      flex: 1;
    }

    /* Each product sits in a soft card with scheme-2 background */
    .upsell-fbt__product {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      text-align: center;
      flex: 1;
    }

    /* Wrap the img-wrap so the checkbox label covers the whole card area */
    .upsell-fbt__product-label {
      display: block;
      position: relative;
      width: 100%;
      cursor: pointer;
      -webkit-user-select: none;
      user-select: none;
    }

    .upsell-fbt__product-img-wrap {
      background: #e9e7df;
      border-radius: 10px;
      padding: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      aspect-ratio: 1;
      position: relative;
    }

    .upsell-fbt__product img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 6px;
      display: block;
    }

    .upsell-fbt__product-placeholder {
      width: 100%;
      aspect-ratio: 1;
      background: var(--color-border, #ddd);
      border-radius: 6px;
    }

    .upsell-fbt__product-title {
      font-size: 0.72rem;
      color: var(--color-foreground);
      line-height: 1.2;
      max-width: 120px;
      margin-top: 4px;
    }

    .upsell-fbt__product-price-sm {
      font-size: 0.72rem;
      font-weight: 600;
      color: var(--color-foreground);
      line-height: 1.2;
    }

    /* "+" sign — JS sets margin-top to center on image */
    .upsell-fbt__plus {
      font-size: 2rem;
      font-weight: 700;
      color: #2D5E5E;
      flex-shrink: 0;
      line-height: 1;
      align-self: flex-start;
    }

    /* CTA column: price → button → checklist, vertically centered with cards */
    .upsell-fbt__cta {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
      flex-shrink: 0;
    }

    .upsell-fbt__price {
      margin: 0;
      line-height: 1.1;
    }

    .upsell-fbt__price-label {
      display: block;
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--color-foreground);
      text-transform: none;
      letter-spacing: 0;
    }

    .upsell-fbt__price-total {
      display: block;
      font-size: 1.4rem;
      font-weight: 800;
      color: var(--color-foreground);
      letter-spacing: -0.01em;
    }

    /* Bundle discount pricing: crossed-out original + highlighted deal price */
    .upsell-fbt__price-amounts {
      display: flex;
      align-items: baseline;
      gap: 8px;
    }

    .upsell-fbt__price-was {
      font-size: 0.95rem;
      font-weight: 600;
      color: var(--color-foreground);
      opacity: 0.55;
      text-decoration: line-through;
    }

    /* Deal price: plain (no background), sits left of the struck-out original */
    .upsell-fbt__price-total--deal {
      color: var(--color-foreground);
    }

    .upsell-fbt__save {
      display: inline-block;
      font-size: 0.72rem;
      font-weight: 700;
      color: #2D5E5E;
      letter-spacing: 0.02em;
    }

    .upsell-fbt__buy {
      white-space: nowrap;
      text-transform: none !important;
      letter-spacing: normal !important;
      font-size: 0.85rem;
      padding-top: calc(var(--button-padding-vertical, 14px) - 5px) !important;
      padding-bottom: calc(var(--button-padding-vertical, 14px) - 5px) !important;
      padding-left: calc(var(--button-padding-horizontal, 24px) + 20px) !important;
      padding-right: calc(var(--button-padding-horizontal, 24px) + 20px) !important;
    }

    /* ── Card checkbox — top-left corner of the image wrap ── */

    /* Hide native checkbox */
    .upsell-fbt__check {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 0;
      width: 0;
    }

    /* Custom checkmark pinned to top-left of img-wrap */
    .upsell-fbt__checkmark {
      position: absolute;
      top: 8px;
      left: 8px;
      height: 20px;
      width: 20px;
      border-radius: 20px;
      background-color: rgba(238,238,238,0.9);
      z-index: 1;
      transition: background-color 0.15s;
    }

    .upsell-fbt__product-label:hover .upsell-fbt__checkmark {
      background-color: rgba(204,204,204,0.95);
    }

    .upsell-fbt__check:checked ~ .upsell-fbt__checkmark {
      background-color: #E9B024;
    }

    .upsell-fbt__checkmark:after {
      content: "";
      position: absolute;
      display: none;
    }

    .upsell-fbt__check:checked ~ .upsell-fbt__checkmark:after {
      display: block;
    }

    .upsell-fbt__checkmark:after {
      left: 7px;
      top: 3px;
      width: 5px;
      height: 9px;
      border: solid white;
      border-width: 0 2px 2px 0;
      -webkit-transform: rotate(45deg);
      -ms-transform: rotate(45deg);
      transform: rotate(45deg);
    }

    /* ── Sold-out bundle item: greyed, not selectable ── */
    .upsell-fbt__product--soldout .upsell-fbt__product-img-wrap {
      filter: grayscale(1);
      opacity: 0.5;
    }
    .upsell-fbt__product--soldout .upsell-fbt__product-label {
      cursor: not-allowed;
      pointer-events: none;
    }
    .upsell-fbt__product--soldout .upsell-fbt__checkmark { display: none; }
    .upsell-fbt__product--soldout .upsell-fbt__product-price-sm {
      text-decoration: line-through;
      opacity: 0.6;
    }
    .upsell-fbt__soldout-badge {
      display: inline-block;
      font-size: 0.62rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #b01020;
      line-height: 1.2;
      margin-top: 1px;
    }


    @media screen and (max-width: 749px) {
      .upsell-fbt__box { flex-direction: column; align-items: flex-start; }
      .upsell-fbt__products { gap: 8px; width: 100%; }
      .upsell-fbt__cta { flex-direction: row; align-items: center; width: 100%; gap: 12px; }
      .upsell-fbt__buy {
        flex: 1;
        padding-left: calc(var(--button-padding-horizontal, 24px) + 11px) !important;
        padding-right: calc(var(--button-padding-horizontal, 24px) + 11px) !important;
      }
      .upsell-fbt__price { flex-shrink: 0; }
    }
  `;
  document.head.appendChild(s);
})();

// ── Checkmark burst SVG (mirrors icon-checkmark-burst.svg) ───────────────────

const CHECKMARK_BURST_SVG = `<svg aria-hidden="true" class="checkmark-burst" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g class="check">
    <circle class="ring" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <path class="tick" d="M9 12.75L11.25 15L15 9.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  </g>
  <g class="burst">
    <g style="--index: 0;"><line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/></g>
    <g style="--index: 1;"><line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/></g>
    <g style="--index: 2;"><line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/></g>
    <g style="--index: 3;"><line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/></g>
    <g style="--index: 4;"><line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/></g>
    <g style="--index: 5;"><line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/></g>
    <g style="--index: 6;"><line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/></g>
    <g style="--index: 7;"><line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/></g>
  </g>
</svg>`;

// ── Shared utilities ──────────────────────────────────────────────────────────

const CONFIG = () => window.UPSELL_CONFIG || {};

// ── Fallback color name → hex map (mirrors SK_AD_COLORS in sk-ad-slot.liquid) ─

const _COLOR_MAP = {
  'black':'#111111','ink black':'#1c1c2e','jet black':'#0a0a0a',
  'white':'#e8e8e8','off white':'#f0ece4','cream':'#f3ede0',
  'navy':'#0f2557','sailor navy':'#0f2557','dark navy':'#0a1a40',
  'blue':'#2563a8','light blue':'#7fb3d3','sky blue':'#87ceeb','royal blue':'#1a4fa0',
  'red':'#c0392b','cherry red':'#b01020','brick red':'#a0321c',
  'green':'#2e7d32','olive':'#6b7c3b','field olive':'#6b7c3b','forest green':'#228b22','sage':'#8fad88',
  'grey':'#777777','gray':'#777777','charcoal':'#3c3c3c','heather grey':'#9a9a9a','light grey':'#c0c0c0',
  'brown':'#6d4c41','tan':'#c8a87a','camel':'#c19a6b','khaki':'#c3b48e',
  'pink':'#d06080','hot pink':'#e91e8c','blush':'#e8b4b8','mauve':'#b07080',
  'purple':'#7b3fa0','lavender':'#9b8bb0','violet':'#6a0dad','plum':'#8e3c8e',
  'orange':'#d4660a','rust':'#b84a18','burnt orange':'#c04a10','coral':'#e06050',
  'yellow':'#d4a500','gold':'#c8960a','mustard':'#c8960a','butter':'#e8d080',
  'teal':'#1a8080','turquoise':'#20b0a0','aqua':'#00b0c0','mint':'#88c8a0',
  'burgundy':'#7d1528','wine':'#6d1020','maroon':'#6d0020',
  'beige':'#d4c4a0','sand':'#c8b880','natural':'#d0c4a8',
  'denim':'#4a5c8a','chambray':'#5c7090','moss':'#7a8a6a',
  'steel':'#6e7e8c','steel blue':'#6e7e8c','steel grey':'#6e7e8c',
  'ivory':'#ece5d6','heather brick':'#8b3a3a','heather mushroom':'#a08070',
  'heather periwinkle':'#7070b0','heather moss':'#7a8a6a','ivory heather':'#ece5d6',
};

function _fallbackColor(name) {
  const k = (name || '').toLowerCase().trim();
  if (_COLOR_MAP[k]) return _COLOR_MAP[k];
  for (const c in _COLOR_MAP) {
    if (k.includes(c) || c.includes(k)) return _COLOR_MAP[c];
  }
  return '#888888';
}

// ── Adult-humor filter (2026-10-23, see snippets/sk-adult-attr.liquid) ────────
// Same tags as the Liquid snippet. Adult products are left out of the bundle and the
// cart recommendations unless this page is adult or the shopper turned them on.
const _ADULT_TAGS = ['adult humor', 'profanity', 'swear words', 'curse words', 'rude & crude', 'inappropriate'];
function _isAdult(product) {
  return (product?.tags || []).some(t => _ADULT_TAGS.includes(String(t).toLowerCase()));
}
function _adultAllowed() {
  return window.skAdult ? window.skAdult.show()
    : /\bsk-adult-(on|ctx)\b/.test(document.documentElement.className);
}
// The bundle asks for a few spare companions so it still has one to show after
// adult (or sold-out) ones are dropped; #renderBundle trims back to data-limit.
function _fbtFetchLimit(limit) {
  return String((parseInt(limit, 10) || 1) + 3);
}

// ── Product variant enrichment (fetches /products/{handle}.js, cached) ────────

const _variantCache = new Map();

async function _enrichProductVariants(product) {
  if (product.variants && product.variants.length > 0 && product.tags) return product;
  if (!product.handle) return product;
  let promise = _variantCache.get(product.handle);
  if (!promise) {
    promise = fetch(`/products/${product.handle}.js`)
      .then(r => r.ok ? r.json() : null)
      .catch(() => null);
    _variantCache.set(product.handle, promise);
  }
  const data = await promise;
  if (!data) return product;
  return {
    ...product,
    tags: product.tags || data.tags || [],
    variants: (product.variants && product.variants.length) ? product.variants : (data.variants || []),
    options: (product.options && product.options.length) ? product.options : (data.options || []),
    images: (data.images || []).map(img => ({
      src: img.src ? (img.src.startsWith('//') ? 'https:' + img.src : img.src) : null,
      variant_ids: img.variant_ids || [],
    })),
  };
}

// ── Stock availability ────────────────────────────────────────────────────────
// A product is purchasable if at least one of its variants is available.
// When variant data is missing (enrichment failed), default to available so we
// never hide a product just because we couldn't load its stock data.

function _isProductAvailable(product) {
  if (Array.isArray(product.variants) && product.variants.length) {
    return product.variants.some(v => v.available);
  }
  return true;
}

// ── Build color chip HTML for a bundle product card ───────────────────────────

function _buildColorChips(product) {
  if (!product.variants || !product.variants.length || !product.options || !product.options.length) return '';

  const colorOpt = product.options.find(o => {
    const n = (o.name || '').toLowerCase();
    return n === 'color' || n === 'colour';
  });
  if (!colorOpt) return '';

  const colorKey = `option${colorOpt.position}`;

  // Aggregate color → first available variant + image
  const colorMap = {};
  for (const v of product.variants) {
    const cn = v[colorKey];
    if (!cn) continue;
    if (!colorMap[cn]) colorMap[cn] = { name: cn, available: false, variantId: v.id, imageUrl: null };
    if (v.available && !colorMap[cn].available) {
      colorMap[cn].available = true;
      colorMap[cn].variantId = v.id;
    }
  }

  // Attach image URLs from the images array
  for (const cn in colorMap) {
    const vid = colorMap[cn].variantId;
    const img = (product.images || []).find(i => i.variant_ids && i.variant_ids.includes(vid));
    if (img && img.src) colorMap[cn].imageUrl = img.src;
  }

  const available = Object.values(colorMap).filter(c => c.available);
  if (available.length <= 1) return ''; // Nothing to pick

  // Which color is currently selected?
  const selVariant = product.variants.find(v => v.id === product.variant_id);
  const selColor = selVariant ? selVariant[colorKey] : (available[0] && available[0].name);

  const chips = available.map(c => {
    const bg = _fallbackColor(c.name);
    const isSel = c.name === selColor;
    const safeColor = c.name.replace(/"/g, '&quot;');
    const safeImg = (c.imageUrl || '').replace(/"/g, '&quot;');
    return `<button type="button" class="sk-cip__chip${isSel ? ' is-sel' : ''}" data-vid="${c.variantId}" data-color="${safeColor}" data-img="${safeImg}" style="background:${bg};"><span class="sk-cip__chip-name">${c.name}</span></button>`;
  }).join('');

  return `<div class="upsell-fbt__chips">${chips}</div>`;
}

// ── Eager parallel fetch — starts as soon as the script loads ─────────────────
// Both the service call and the Shopify fallback fire at the same time so there
// is no sequential waterfall. Results are stored in sessionStorage so repeat
// visits within the same tab are instant.

const _fbtPromises = new Map();

function _shopifyFallbackFetch(productId, limit) {
  return fetch(`/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`)
    .then(r => r.ok ? r.json() : null)
    .then(data => {
      const products = data?.products;
      if (!products?.length) return null;
      return products.map(p => {
        const rawImg = p.featured_image || p.images?.[0] || null;
        return {
          id: p.id,
          title: p.title,
          handle: p.handle,
          url: `/products/${p.handle}`,
          image_url: rawImg ? (rawImg.startsWith('//') ? 'https:' + rawImg : rawImg) : null,
          price: (p.price || 0) / 100,
          variant_id: p.variants?.[0]?.id || null,
          tags: p.tags || [],
          // Preserve full variant/option/image data for swatch chips
          variants: p.variants || [],
          options: p.options || [],
          images: (p.images || []).map(img => ({
            src: img.src ? (img.src.startsWith('//') ? 'https:' + img.src : img.src) : null,
            variant_ids: img.variant_ids || [],
          })),
        };
      });
    })
    .catch(() => null);
}

function _startFbtFetch(productId, limit) {
  const key = `fbt_${productId}_${limit}`;
  if (_fbtPromises.has(key)) return;

  // Check sessionStorage first — instant render on repeat visits
  try {
    const hit = sessionStorage.getItem(key);
    if (hit) { _fbtPromises.set(key, Promise.resolve(JSON.parse(hit))); return; }
  } catch {}

  const serviceUrl = CONFIG().serviceUrl;

  // Fire both requests simultaneously — no waterfall
  const serviceP = serviceUrl
    ? fetch(`${serviceUrl}/upsell/fbt?product_id=${productId}&limit=${limit}`)
        .then(r => r.ok ? r.json() : null).catch(() => null)
    : Promise.resolve(null);

  const shopifyP = _shopifyFallbackFetch(productId, limit);

  const combined = Promise.all([serviceP, shopifyP]).then(([svc, shopify]) => {
    // Prefer real FBT data from our service
    if (svc?.source === 'fbt' && svc.products?.length) {
      try { sessionStorage.setItem(key, JSON.stringify({ products: svc.products })); } catch {}
      return { products: svc.products };
    }
    // Fall back to Shopify (already resolved — no extra wait)
    if (shopify?.length) {
      try { sessionStorage.setItem(key, JSON.stringify({ products: shopify })); } catch {}
      return { products: shopify };
    }
    return null;
  });

  _fbtPromises.set(key, combined);
}

// Kick off immediately for the element already in the DOM (script is type=module,
// so DOM is already parsed when this runs)
;(() => {
  const el = document.querySelector('upsell-fbt[data-product-id]');
  if (el) _startFbtFetch(el.dataset.productId, _fbtFetchLimit(el.dataset.limit || '1'));
})();

function formatMoney(cents) {
  const fractionDigits = cents % 100 === 0 ? 0 : 2;
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

// ── GA4 upsell tracking ───────────────────────────────────────────────────────
// Fires standard GA4 ecommerce events to the store's existing gtag so each upsell
// surface shows up as its own item_list (impressions → clicks → add-to-cart →
// attributed revenue). Every call is null-guarded and try/caught so a tracking
// failure can never interfere with the actual add-to-cart.

const GA_LISTS = {
  fbt:      { id: 'pdp_fbt',       name: 'PDP Frequently Bought Together' },
  cart:     { id: 'cart_recos',    name: 'Cart Recommendations' },
  featured: { id: 'cart_featured', name: 'Cart Featured' },
};

// Bundle discount: the Shopify Function (bundle-discount-fn) gives this % off any
// cart line tagged _upsell_source=pdp_fbt. Keep in sync with BUNDLE_PERCENTAGE in
// customer-account-ext/extensions/bundle-discount-fn/src/cart_lines_discounts_generate_run.js.
// Only applies (and is only shown) when 2+ bundle items are selected.
const BUNDLE_DISCOUNT_PCT = 10;

// Resolve gtag now, or shortly after — widgets can render from sessionStorage
// cache before the async gtag/js finishes loading, which would otherwise drop
// the impression. Fires synchronously when gtag is already present (so click
// events on links that navigate away aren't lost), else polls briefly.
function _withGtag(cb) {
  try {
    if (typeof window === 'undefined') return;
    if (typeof window.gtag === 'function') { cb(window.gtag); return; }
    let tries = 0;
    const t = setInterval(function () {
      if (typeof window.gtag === 'function') { clearInterval(t); try { cb(window.gtag); } catch (e) {} }
      else if (++tries > 40) { clearInterval(t); } // give up after ~10s
    }, 250);
  } catch (e) {}
}

function _gaItem(p, list, index) {
  const priceCents = p.price_cents != null
    ? p.price_cents
    : (typeof p.price === 'number' ? Math.round(p.price * 100) : 0);
  return {
    item_id: String(p.id || p.variant_id || ''),
    item_name: p.title || '',
    price: priceCents / 100,
    index: index,
    quantity: 1,
    item_list_id: list.id,
    item_list_name: list.name,
  };
}

function gaViewList(list, products) {
  if (!products || !products.length) return;
  _withGtag(function (g) {
    g('event', 'view_item_list', {
      item_list_id: list.id,
      item_list_name: list.name,
      items: products.map((p, i) => _gaItem(p, list, i)),
    });
  });
}

function gaSelect(list, p, index) {
  if (!p) return;
  _withGtag(function (g) {
    g('event', 'select_item', {
      item_list_id: list.id,
      item_list_name: list.name,
      items: [_gaItem(p, list, index || 0)],
    });
  });
}

function gaAddToCart(list, products) {
  if (!products || !products.length) return;
  _withGtag(function (g) {
    const items = products.map((p, i) => _gaItem(p, list, i));
    const value = items.reduce((s, it) => s + (it.price || 0) * (it.quantity || 1), 0);
    g('event', 'add_to_cart', {
      currency: 'USD',
      value: Math.round(value * 100) / 100,
      item_list_id: list.id,
      item_list_name: list.name,
      items: items,
    });
  });
}

/**
 * Add multiple variant IDs to cart in a single request.
 * @param {Array<{id: number, quantity: number}>} items
 * @param {string} [source] - tags each line with a hidden `_upsell_source`
 *   property so the order records which upsell widget drove the add. Line-item
 *   properties prefixed with `_` are hidden from the customer but stored on the
 *   order, enabling exact upsell-revenue attribution from order data.
 */
async function addItemsToCart(items, source) {
  const payloadItems = source
    ? items.map((it) => ({ ...it, properties: { ...(it.properties || {}), _upsell_source: source } }))
    : items;
  const resp = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: payloadItems }),
  });
  if (!resp.ok) throw new Error(`Add to cart failed: ${resp.status}`);
  return resp.json();
}

function openCartDrawer() {
  document.dispatchEvent(new CustomEvent('cart:open'));
  document.dispatchEvent(new CustomEvent('on:cart:open'));
}

// ── <upsell-fbt> — bundle box layout ─────────────────────────────────────────

class UpsellFbt extends HTMLElement {
  connectedCallback() {
    this.#load();
  }

  async #load() {
    const { productId } = this.dataset;
    const limit = _fbtFetchLimit(this.dataset.limit || '1');
    if (!productId) return;

    const key = `fbt_${productId}_${limit}`;

    // Ensure fetch is running (may already be started by eager init above)
    _startFbtFetch(productId, limit);

    try {
      const result = await _fbtPromises.get(key);
      if (result?.products?.length) {
        // Enrich products with full variant data (for swatch chips)
        const enriched = await Promise.all(result.products.map(p => _enrichProductVariants(p)));
        this.#renderBundle(enriched);
      } else {
        this.#hide();
      }
    } catch {
      this.#hide();
    }
  }

  // The PDP's own add-to-cart form. Must be scoped: quick-add cards and the cart
  // drawer's featured-product upsells also render `[data-type="add-to-cart-form"]`
  // forms, and when the cart has items the drawer's forms appear EARLIER in the
  // DOM — a page-wide first-match query then returns the wrong product's variant
  // (bug: Buy Bundle added the drawer's featured item instead of the PDP product).
  // The main PDP form is the only one with data-product-url on its
  // product-form-component; also match this widget's product id for safety.
  #pdpVariantInput() {
    return (
      document.querySelector(
        `product-form-component[data-product-id="${this.dataset.productId}"][data-product-url] input[name="id"]`
      ) ||
      document.querySelector('product-form-component[data-product-url] [data-type="add-to-cart-form"] input[name="id"]')
    );
  }

  #renderBundle(bundleProducts) {
    const heading = this.dataset.heading || 'Frequently Bought Together';
    const buttonText = this.dataset.buttonText || 'Buy Bundle';

    // Read the currently selected variant from the PDP form (may differ from page-load default)
    const pdpInput = this.#pdpVariantInput();
    const currentVariantId = pdpInput
      ? parseInt(pdpInput.value, 10)
      : (this.dataset.productVariantId ? parseInt(this.dataset.productVariantId, 10) : null);

    // Drop out-of-stock companion products entirely — a sold-out item can't be
    // "bought together". If that leaves nothing to bundle, hide the whole widget.
    const want = parseInt(this.dataset.limit || '1', 10) || 1;
    const availableBundle = bundleProducts
      .filter(_isProductAvailable)
      .filter(p => _adultAllowed() || !_isAdult(p))
      .slice(0, want);
    if (!availableBundle.length) {
      this.#hide();
      return;
    }

    const currentProduct = {
      title: this.dataset.productTitle || '',
      url: this.dataset.productUrl || '#',
      image_url: this.dataset.productImage || null,
      price: parseInt(this.dataset.productPrice || '0', 10) / 100,
      variant_id: currentVariantId,
      // The PDP product stays visible (it's the anchor) but is flagged sold-out
      // so its checkbox is disabled and it's excluded from the bundle total.
      _available: this.dataset.productAvailable !== 'false' && !!currentVariantId,
    };

    const allProducts = [
      currentProduct,
      ...availableBundle.map(p => ({ ...p, _available: true })),
    ];

    // Product thumbnails with "+" separators and top-left checkbox
    const thumbsHtml = allProducts.map((p, i) => {
      const plus = i < allProducts.length - 1
        ? '<span class="upsell-fbt__plus" aria-hidden="true">+</span>'
        : '';
      const imgInner = p.image_url
        ? `<img src="${p.image_url}" alt="${p.title}" loading="lazy" width="100" height="100" class="upsell-fbt__product-img">`
        : `<div class="upsell-fbt__product-placeholder"></div>`;

      const chipsHtml = '';
      const soldout = p._available === false;

      return `
        <div class="upsell-fbt__product${soldout ? ' upsell-fbt__product--soldout' : ''}" data-selected-vid="${p.variant_id || ''}">
          <label class="upsell-fbt__product-label">
            <div class="upsell-fbt__product-img-wrap">
              <input
                type="checkbox"
                class="upsell-fbt__check"
                data-index="${i}"
                data-price="${Math.round((p.price || 0) * 100)}"
                data-variant-id="${p.variant_id || ''}"
                ${soldout ? 'disabled' : 'checked'}
              >
              <span class="upsell-fbt__checkmark"></span>
              ${imgInner}
            </div>
          </label>
          <span class="upsell-fbt__product-title">${p.title}</span>
          ${soldout
            ? `<span class="upsell-fbt__soldout-badge">Sold out</span>`
            : `<span class="upsell-fbt__product-price-sm">${formatMoney(Math.round((p.price || 0) * 100))}</span>`}
        </div>
        ${plus}
      `;
    }).join('');

    // Total reflects only the items that start checked (in-stock).
    const totalCents = allProducts
      .filter(p => p._available !== false)
      .reduce((sum, p) => sum + Math.round((p.price || 0) * 100), 0);

    const headingTag = this.dataset.headingTag || 'h2';
    this.innerHTML = `
      <div class="upsell-fbt__inner">
        <div class="upsell-fbt__head-row">
          <${headingTag} class="upsell-fbt__heading">${heading}</${headingTag}>
          <span class="upsell-fbt__pill" hidden>Save ${BUNDLE_DISCOUNT_PCT}%</span>
        </div>
        <div class="upsell-fbt__box">
          <div class="upsell-fbt__products">${thumbsHtml}</div>
          <div class="upsell-fbt__cta">
            <p class="upsell-fbt__price">
              <span class="upsell-fbt__price-label">Total Price:</span>
              <span class="upsell-fbt__price-amounts">
                <span class="upsell-fbt__price-total">${formatMoney(totalCents)}</span>
                <s class="upsell-fbt__price-was" hidden></s>
              </span>
              <span class="upsell-fbt__save" hidden></span>
            </p>
            <button
              class="upsell-fbt__buy button button--primary add-to-cart-button"
            >
              <span class="add-to-cart-text">
                <span class="add-to-cart-text__content">${buttonText}</span>
              </span>
              <span class="add-to-cart__added">
                <span class="svg-wrapper add-to-cart__added-icon">${CHECKMARK_BURST_SVG}</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    `;

    this.removeAttribute('hidden');

    // GA4 impression — the recommended companions shown in the bundle
    gaViewList(GA_LISTS.fbt, availableBundle);

    // Position the + sign to vertically center on the card image, not the full card
    const alignPlus = () => {
      const imgWrap = this.querySelector('.upsell-fbt__product-img-wrap');
      const plus = this.querySelector('.upsell-fbt__plus');
      if (imgWrap && plus) {
        const imgH = imgWrap.getBoundingClientRect().height;
        const plusH = plus.getBoundingClientRect().height;
        plus.style.marginTop = Math.max(0, (imgH - plusH) / 2) + 'px';
      }
    };
    requestAnimationFrame(alignPlus);
    window.addEventListener('resize', alignPlus);

    const priceEl = this.querySelector('.upsell-fbt__price-total');
    const wasEl = this.querySelector('.upsell-fbt__price-was');
    const saveEl = this.querySelector('.upsell-fbt__save');
    const pillEl = this.querySelector('.upsell-fbt__pill');
    const buyBtn = this.querySelector('.upsell-fbt__buy');

    // Sync total price when checkboxes change. With 2+ items selected the bundle
    // discount applies (enforced at checkout by the bundle-discount-fn Shopify
    // Function), so show crossed-out original + highlighted discounted price.
    const syncPrice = () => {
      const checked = [...this.querySelectorAll('.upsell-fbt__check')].filter(c => c.checked);
      const selectedTotal = checked.reduce((sum, c) => sum + parseInt(c.dataset.price, 10), 0);
      const isBundle = checked.length >= 2;

      if (isBundle) {
        const discounted = Math.round(selectedTotal * (100 - BUNDLE_DISCOUNT_PCT) / 100);
        wasEl.textContent = formatMoney(selectedTotal);
        wasEl.hidden = false;
        priceEl.textContent = formatMoney(discounted);
        priceEl.classList.add('upsell-fbt__price-total--deal');
        saveEl.textContent = `Save ${BUNDLE_DISCOUNT_PCT}% (${formatMoney(selectedTotal - discounted)}) with this bundle`;
        saveEl.hidden = false;
        if (pillEl) pillEl.hidden = false;
      } else {
        wasEl.hidden = true;
        priceEl.textContent = formatMoney(selectedTotal);
        priceEl.classList.remove('upsell-fbt__price-total--deal');
        saveEl.hidden = true;
        if (pillEl) pillEl.hidden = true;
      }
    };

    this.querySelectorAll('.upsell-fbt__check').forEach(cb =>
      cb.addEventListener('change', syncPrice)
    );

    // Initial paint (all in-stock items start checked, so the bundle deal shows)
    syncPrice();

    buyBtn.addEventListener('click', () => {
      this.#handleBundleClick(buyBtn);
    });
  }

  #handleBundleClick(button) {
    // Collect selected variant IDs from checked product cards
    const cards = [...this.querySelectorAll('.upsell-fbt__product')];

    const items = [];
    cards.forEach((card, i) => {
      const cb = card.querySelector('.upsell-fbt__check');
      if (!cb || !cb.checked) return;

      let vid;
      if (i === 0) {
        // Current PDP product — read live from the PDP's own form (scoped; see
        // #pdpVariantInput) in case the user changed variant
        const pdpInput = this.#pdpVariantInput();
        vid = pdpInput ? parseInt(pdpInput.value, 10) : parseInt(card.dataset.selectedVid || cb.dataset.variantId || '0', 10);
      } else {
        // Upsell products — use data-selected-vid (updated by chip clicks)
        vid = parseInt(card.dataset.selectedVid || cb.dataset.variantId || '0', 10);
      }
      if (vid) items.push({ id: vid, quantity: 1 });
    });

    if (!items.length) return;

    // GA4 add_to_cart — build item detail from the checked bundle cards
    const gaProducts = [];
    cards.forEach((card) => {
      const cb = card.querySelector('.upsell-fbt__check');
      if (!cb || !cb.checked) return;
      gaProducts.push({
        variant_id: cb.dataset.variantId,
        title: card.querySelector('.upsell-fbt__product-title')?.textContent || '',
        price_cents: parseInt(cb.dataset.price, 10) || 0,
      });
    });
    gaAddToCart(GA_LISTS.fbt, gaProducts);

    // ── 1. Trigger checkmark burst animation immediately (same as main ATC) ──
    button.dataset.added = 'true';
    const resetTimeout = setTimeout(() => {
      button.removeAttribute('data-added');
    }, 1200);

    // ── 2. Fly-to-cart animation ──────────────────────────────────────────────
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.productImage;
    if (cartIcon && image) {
      const flyEl = document.createElement('fly-to-cart');
      flyEl.classList.add('fly-to-cart--main');
      flyEl.style.setProperty('background-image', `url(${image})`);
      flyEl.style.setProperty('--start-opacity', '0');
      flyEl.source = button;
      flyEl.destination = cartIcon;
      document.body.appendChild(flyEl);
    }

    // ── 3. Add items to cart + update cart UI ────────────────────────────────
    button.disabled = true;

    // Only tag as a bundle (which triggers the 10% Function discount) when 2+
    // items are actually being added; a single item is not a bundle, gets no tag,
    // no discount, and the display above shows full price for that case too.
    addItemsToCart(items, items.length >= 2 ? GA_LISTS.fbt.id : undefined)
      .then(() => {
        // Dispatch cart:update so the cart icon count increments immediately
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: {},
            data: {
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
            },
          },
        }));
        openCartDrawer();
      })
      .catch(() => {
        clearTimeout(resetTimeout);
        button.removeAttribute('data-added');
        const textEl = button.querySelector('.add-to-cart-text__content');
        if (textEl) textEl.textContent = 'Error — try again';
      })
      .finally(() => {
        button.disabled = false;
      });
  }

  #hide() {
    this.setAttribute('hidden', '');
  }
}

if (!customElements.get('upsell-fbt')) {
  customElements.define('upsell-fbt', UpsellFbt);
}


// ── Cart card helpers ─────────────────────────────────────────────────────────

function buildCardHtml(product) {
  const price = product.price != null ? formatMoney(Math.round(product.price * 100)) : '';
  return `
    <div class="upsell-card">
      <a href="${product.url}" class="upsell-card__image-link">
        ${product.image_url
          ? `<img src="${product.image_url}" alt="${product.title}" loading="lazy" width="300" height="300">`
          : `<div class="upsell-card__image-placeholder"></div>`
        }
      </a>
      <div class="upsell-card__info">
        <a href="${product.url}" class="upsell-card__title">${product.title}</a>
        ${price ? `<span class="upsell-card__price">${price}</span>` : ''}
      </div>
      ${product.variant_id
        ? `<button class="upsell-card__atc button button--primary" data-variant-id="${product.variant_id}">Add to Cart</button>`
        : `<a href="${product.url}" class="upsell-card__view button button--secondary">View Product</a>`
      }
    </div>
  `;
}

function handleAtcClick(button) {
  const variantId = parseInt(button.dataset.variantId, 10);
  if (!variantId) return;
  button.disabled = true;
  button.textContent = 'Adding…';
  addItemsToCart([{ id: variantId, quantity: 1 }], GA_LISTS.cart.id)
    .then(() => {
      button.textContent = 'Added!';
      openCartDrawer();
    })
    .catch(() => {
      button.textContent = 'Error — try again';
      button.disabled = false;
    });
}

// ── <upsell-cart> ─────────────────────────────────────────────────────────────

(function injectUpsellCartStyles() {
  if (document.getElementById('upsell-cart-styles')) return;
  const s = document.createElement('style');
  s.id = 'upsell-cart-styles';
  s.textContent = `
    .upsell-cart__section-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
    }

    .upsell-cart__heading {
      font-size: 0.75rem;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--color-foreground);
      margin: 0;
    }

    .upsell-cart__nav { display: flex; gap: 4px; }

    .upsell-cart__nav-btn {
      background: none;
      border: 1px solid var(--color-border, #e5e5e5);
      border-radius: 50%;
      width: 26px;
      height: 26px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--color-foreground);
      transition: background 0.15s;
      padding: 0;
    }

    .upsell-cart__nav-btn:hover:not(:disabled) {
      background: var(--color-border, #e5e5e5);
    }

    .upsell-cart__nav-btn:disabled { opacity: 0.3; cursor: default; }

    .upsell-cart__carousel { overflow: hidden; }

    .upsell-cart__track {
      display: flex;
      transition: transform 0.3s ease;
      user-select: none;
    }

    .upsell-cart__slide {
      display: flex;
      gap: 8px;
      min-width: 100%;
    }

    .upsell-cart__card {
      flex: 1;
      display: flex;
      gap: 10px;
      align-items: center;
      background: var(--color-background-2, #f5f5f5);
      border-radius: 8px;
      padding: 8px;
      min-width: 0;
    }

    .upsell-cart__card-img {
      width: 56px;
      height: 56px;
      object-fit: cover;
      border-radius: 6px;
      flex-shrink: 0;
    }

    .upsell-cart__card-info { flex: 1; min-width: 0; }

    .upsell-cart__card-title {
      font-size: 0.72rem;
      font-weight: 600;
      color: var(--color-foreground);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 2px;
    }

    .upsell-cart__card-price {
      font-size: 0.72rem;
      color: var(--color-foreground);
      margin-bottom: 6px;
    }

    .upsell-cart__card-atc {
      font-size: 0.68rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      padding: 4px 10px;
      background: var(--color-button, #000);
      color: var(--color-button-text, #fff);
      border: none;
      border-radius: 4px;
      cursor: pointer;
      transition: opacity 0.15s;
      white-space: nowrap;
    }

    .upsell-cart__card-atc:hover { opacity: 0.8; }
    .upsell-cart__card-atc:disabled { opacity: 0.5; cursor: default; }
    /* "Customize" variant is an anchor — match the button look */
    a.upsell-cart__card-atc {
      display: inline-block;
      text-decoration: none;
      text-align: center;
    }
  `;
  document.head.appendChild(s);
})();

function _shopifyImgSrc(rawImg, width) {
  if (!rawImg) return null;
  const src = typeof rawImg === 'string'
    ? (rawImg.startsWith('//') ? 'https:' + rawImg : rawImg)
    : (rawImg.src ? 'https:' + rawImg.src : null);
  if (!src) return null;
  return src.includes('?') ? `${src}&width=${width}` : `${src}?width=${width}`;
}

class UpsellCart extends HTMLElement {
  #page = 0;
  #recs = [];
  #slides = [];
  #reloading = false;
  #everShown = false;
  #cartUpdateListener = null;
  #externalPrev = null;
  #externalNext = null;

  // Detect when morphdom restores 'hidden' after a section re-render
  static get observedAttributes() { return ['hidden']; }

  attributeChangedCallback(name, _oldValue, newValue) {
    if (name === 'hidden' && newValue !== null && this.#everShown && this.isConnected) {
      // Widget was visible; something external re-hid it — reload after the patch settles
      setTimeout(() => {
        if (this.isConnected && this.hasAttribute('hidden')) this.#load();
      }, 80);
    }
  }

  connectedCallback() {
    this.#load();
    this.#cartUpdateListener = (e) => {
      if (e.detail?._source === 'upsell') return;
      this.#reload();
    };
    document.addEventListener('cart:update', this.#cartUpdateListener);
  }

  disconnectedCallback() {
    if (this.#cartUpdateListener) {
      document.removeEventListener('cart:update', this.#cartUpdateListener);
      this.#cartUpdateListener = null;
    }
  }

  async #load() {
    this.#page = 0;

    const heading = this.dataset.heading || 'Equally Awesome';
    const limit = parseInt(this.dataset.limit || '6', 10);

    // Fetch cart state
    let cartProductIds = new Set();
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      (cart.items || []).forEach(i => { if (i.product_id) cartProductIds.add(i.product_id); });
    } catch {}

    // Fetch recommendations — try complementary first, fall back to related
    let recProducts = [];
    if (cartProductIds.size > 0) {
      const firstId = [...cartProductIds][0];
      for (const intent of ['complementary', 'related']) {
        try {
          const data = await fetch(
            `/recommendations/products.json?product_id=${firstId}&limit=${limit}&intent=${intent}`
          ).then(r => r.ok ? r.json() : null);
          if (data?.products?.length) {
            recProducts = data.products
              .filter(p => !cartProductIds.has(p.id))
              .filter(p => _adultAllowed() || !_isAdult(p))
              .map(p => ({
                id: p.id,
                title: p.title,
                url: `/products/${p.handle}`,
                image_url: _shopifyImgSrc(p.featured_image || p.images?.[0] || null, 120),
                price: formatMoney(p.price || 0),
                price_cents: p.price || 0,
                // Default to the first IN-STOCK variant. Products with multiple
                // sizes/colors often have a sold-out variant in position 0, and
                // adding that variant makes /cart/add.js return a 422 error.
                variant_id: ((p.variants || []).find(v => v.available) || (p.variants || [])[0] || {}).id || null,
                // Custom products must be personalized on the PDP — no quick-add
                // (same 'Custom' tag detection as collection cards / carousels)
                custom: (p.tags || []).some(t => String(t).toLowerCase() === 'custom'),
              }));
            if (recProducts.length) break;
          }
        } catch {}
      }
    }

    if (!recProducts.length) {
      this.setAttribute('hidden', '');
      return;
    }

    this.#render(heading, recProducts);
    this.#everShown = true;
    this.removeAttribute('hidden');
  }

  #render(heading, recs) {
    this.#recs = recs;
    this.#slides = [];
    for (let i = 0; i < recs.length; i += 2) this.#slides.push(recs.slice(i, i + 2));

    const multiPage = this.#slides.length > 1;
    const useExternalNav = !!this.dataset.externalNav;

    // Wire up external nav buttons
    if (useExternalNav) {
      const [prevId, nextId] = (this.dataset.externalNav || '').split(',').map(s => s.trim());
      this.#externalPrev = document.getElementById(prevId) || null;
      this.#externalNext = document.getElementById(nextId) || null;
      // Show/hide nav via class so CSS tab rules can still control visibility
      const navEl = this.#externalPrev?.closest('.sk-upsell-ext-nav');
      if (navEl) navEl.classList.toggle('sk-upsell-ext-nav--active', multiPage);
    }

    this.innerHTML = `<div>
      ${!useExternalNav ? `
        <div class="upsell-cart__section-head">
          <h3 class="upsell-cart__heading">${heading}</h3>
          ${multiPage ? `
            <div class="upsell-cart__nav">
              <button class="upsell-cart__nav-btn" data-dir="-1" aria-label="Previous" disabled>
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M7.5 2L3.5 6L7.5 10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <button class="upsell-cart__nav-btn" data-dir="1" aria-label="Next">
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M4.5 2L8.5 6L4.5 10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
            </div>
          ` : ''}
        </div>
      ` : ''}
      <div class="upsell-cart__carousel">
        <div class="upsell-cart__track">
          ${this.#slides.map((slide, si) => `
            <div class="upsell-cart__slide">
              ${slide.map((p, pi) => {
                const gi = si * 2 + pi;
                return `
                <div class="upsell-cart__card">
                  ${p.image_url ? `<img class="upsell-cart__card-img" src="${p.image_url}" alt="${p.title}" loading="lazy">` : ''}
                  <div class="upsell-cart__card-info">
                    <div class="upsell-cart__card-title">${p.title}</div>
                    <div class="upsell-cart__card-price">${p.price}</div>
                    ${p.custom
                      ? `<a class="upsell-cart__card-atc upsell-cart__card-atc--customize" href="${p.url}" data-rec-index="${gi}">Customize</a>`
                      : `<button class="upsell-cart__card-atc" data-variant-id="${p.variant_id}" data-rec-index="${gi}">Add</button>`}
                  </div>
                </div>
              `;}).join('')}
            </div>
          `).join('')}
        </div>
      </div>
    </div>`;

    this.#bindEvents();
    this.#updateTrack();

    // GA4 impression — the recommendations shown in the cart
    gaViewList(GA_LISTS.cart, recs);
  }

  #bindEvents() {
    this.querySelectorAll('.upsell-cart__nav-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const dir = parseInt(btn.dataset.dir, 10);
        this.#page = Math.max(0, Math.min(this.#slides.length - 1, this.#page + dir));
        this.#updateTrack();
      });
    });

    // External nav — use onclick to prevent duplicate listeners on re-render
    if (this.#externalPrev) {
      this.#externalPrev.onclick = () => {
        this.#page = Math.max(0, this.#page - 1);
        this.#updateTrack();
      };
    }
    if (this.#externalNext) {
      this.#externalNext.onclick = () => {
        this.#page = Math.min(this.#slides.length - 1, this.#page + 1);
        this.#updateTrack();
      };
    }

    // Buttons only — the "Customize" variant is an <a> that navigates to the PDP
    this.querySelectorAll('button.upsell-cart__card-atc').forEach(btn => {
      btn.addEventListener('click', () => this.#handleAtc(btn));
    });

    // "Customize" anchors navigate to the PDP — count as a select_item click-through
    this.querySelectorAll('a.upsell-cart__card-atc').forEach(a => {
      a.addEventListener('click', () => {
        const idx = parseInt(a.dataset.recIndex, 10);
        gaSelect(GA_LISTS.cart, this.#recs[idx], idx);
      });
    });
  }

  #updateTrack() {
    const track = this.querySelector('.upsell-cart__track');
    if (track) track.style.transform = `translateX(-${this.#page * 100}%)`;

    this.querySelectorAll('.upsell-cart__nav-btn').forEach(btn => {
      const dir = parseInt(btn.dataset.dir, 10);
      btn.disabled = dir < 0 ? this.#page === 0 : this.#page >= this.#slides.length - 1;
    });

    // Update external nav button states
    if (this.#externalPrev) this.#externalPrev.disabled = this.#page === 0;
    if (this.#externalNext) this.#externalNext.disabled = this.#page >= this.#slides.length - 1;
  }

  async #handleAtc(btn) {
    const variantId = parseInt(btn.dataset.variantId, 10);
    if (!variantId) return;

    // GA4 add_to_cart for the clicked recommendation
    const recIdx = parseInt(btn.dataset.recIndex, 10);
    if (!Number.isNaN(recIdx) && this.#recs[recIdx]) {
      gaAddToCart(GA_LISTS.cart, [this.#recs[recIdx]]);
    }

    const orig = btn.textContent;
    btn.disabled = true;
    btn.textContent = '...';
    try {
      await addItemsToCart([{ id: variantId, quantity: 1 }], GA_LISTS.cart.id);
      btn.textContent = 'Added!';
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { _source: 'upsell', resource: {}, data: {} },
      }));
      setTimeout(() => this.#reload(), 800);
    } catch {
      btn.textContent = 'Error';
      setTimeout(() => { btn.textContent = orig; btn.disabled = false; }, 1500);
    }
  }

  #reload() {
    if (this.#reloading) return;
    this.#reloading = true;
    this.#load().finally(() => { this.#reloading = false; });
  }
}

if (!customElements.get('upsell-cart')) {
  customElements.define('upsell-cart', UpsellCart);
}

// ── Featured-tab (cart drawer) GA tracking ────────────────────────────────────
// The Featured tab's cards are Liquid product forms (theme add-to-cart path),
// so GA events are wired here via delegation. Order-level attribution comes from
// the hidden properties[_upsell_source]=cart_featured input in upsell-feat-card.

(function trackCartFeatured() {
  function cardData(card) {
    return {
      id: card.dataset.productId,
      title: card.dataset.productTitle || '',
      price_cents: parseInt(card.dataset.productPrice || '0', 10),
    };
  }

  // add_to_cart: any Add button inside a featured card
  document.addEventListener('click', function (e) {
    const btn = e.target && e.target.closest && e.target.closest('[data-sk-featured] button[name="add"]');
    if (!btn || btn.disabled) return;
    const card = btn.closest('[data-sk-featured]');
    if (card) gaAddToCart(GA_LISTS.featured, [cardData(card)]);
  }, { capture: true, passive: true });

  // view_item_list: once per page, the first time the cart drawer opens
  // (Featured is the default-visible tab).
  let viewed = false;
  function fireView() {
    if (viewed) return;
    const cards = [...document.querySelectorAll('[data-sk-featured]')];
    if (!cards.length) return;
    viewed = true;
    gaViewList(GA_LISTS.featured, cards.map(cardData));
  }
  ['cart:open', 'on:cart:open', 'cart:update'].forEach(function (evt) {
    document.addEventListener(evt, function () { setTimeout(fireView, 300); });
  });
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof MutationObserver === 'function') {
    const dlg = drawer.querySelector('dialog');
    if (dlg) {
      new MutationObserver(function () {
        if (dlg.open) fireView();
      }).observe(dlg, { attributes: true, attributeFilter: ['open'] });
    }
  }
})();
